// Whether an anchor page's CONTENT is in scope for assessment generation. Separate from
// who is viewing (the generate-assessment visibility flag) and from whether the server
// accepts the request: an out-of-scope page still shows its Generate tile, disabled with
// the reason, so a teacher learns their goal is not covered yet.

// Mirrors the subject clusters Threads generates for
// (frontend/src/components/assessment/isGenerateAssessmentAvailable.js), with two product
// decisions on top: 'Adapted Physical Education' is admitted as motor-adjacent although
// Threads has no counterpart for it, and the three Behavior spellings travel together
// because they are per-content-profile wording for one subject. Threads areas with no
// Toolkit subject (Specific Learning Disability's oral expression and listening
// comprehension, and the Communication & Language Skills, ASL and English communication
// areas) are not mapped. Everything else fails closed, so a subject Content adds later is
// not silently opted in.
export const GENERATION_SUBJECTS = [
  'Reading',
  'Writing',
  'Math',
  'Behavior & SEL',
  'Behavior Skills',
  'Resiliency Skills',
  'Executive Functioning',
  'Fine Motor',
  'Gross Motor',
  'Adapted Physical Education',
  'Pre-Academics',
  'Life Skills',
  'Transition',
  'Speech & Language',
  'Assistive Technology'
]

// Admitted for their ELA and Math content areas only. The content area is a DOMAIN on the
// same alignment, not a subject, so the family cannot simply join GENERATION_SUBJECTS --
// that would also admit its Self Care and Community goals. 'Life Skills' is not in the
// family: it is admitted as a subject in its own right above.
export const ALT_ACADEMICS_SUBJECTS = ['Alt Academics', 'Alt Academic & Life Skills']

// One flat list rather than one per subject spelling, because the domain spelling comes
// from the district's domain config: common-core districts serve 'Alt Academics' with the
// prefixed spellings and Florida with the bare ones.
export const ALT_ACADEMICS_DOMAINS = ['ELA', 'Math', 'Academics: ELA', 'Academics: Math']

// Mirrors an Airtable-owned characteristic id (goalbook-go-common/characteristics).
export const BLIND_LOW_VISION_CHARACTERISTIC = 'recjUHa6goKzM1jnr'
export const BLIND_LOW_VISION_LABEL = 'Blind/Low Vision'

export const UNNAMED_SUBJECT_REASON = 'this goal'

export interface SubjectAlignmentContent {
  subject?: unknown
  domains?: unknown
}

export interface GenerationScopeContent {
  subject_alignments?: unknown
  characteristics?: unknown
}

interface NamedSubjectAlignment {
  subject: string
  domains?: unknown
}

// Case- and whitespace-insensitive, as Threads compares its goal areas, so ' reading' is
// not refused as an unsupported subject.
function listIncludes(list: string[], value: string): boolean {
  const normalizedValue = value.trim().toLowerCase()
  return list.some((entry) => entry.toLowerCase() === normalizedValue)
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : []
}

function isNamedSubjectAlignment(alignment: unknown): alignment is NamedSubjectAlignment {
  if (!alignment || typeof alignment !== 'object') {
    return false
  }
  const { subject } = alignment as SubjectAlignmentContent
  return typeof subject === 'string' && subject.trim().length > 0
}

function hasAdmittedAltAcademicsDomain(alignment: NamedSubjectAlignment): boolean {
  return asArray(alignment.domains).some(
    (domain) => typeof domain === 'string' && listIncludes(ALT_ACADEMICS_DOMAINS, domain)
  )
}

// Judged per alignment rather than over flattened subject and domain lists, so an
// 'Academics: ELA' domain on one alignment cannot admit an 'Alt Academics' subject on another.
function isAlignmentInScope(alignment: NamedSubjectAlignment): boolean {
  if (listIncludes(GENERATION_SUBJECTS, alignment.subject)) {
    return true
  }
  const isAltAcademicsSubject = listIncludes(ALT_ACADEMICS_SUBJECTS, alignment.subject)
  return isAltAcademicsSubject && hasAdmittedAltAcademicsDomain(alignment)
}

function isBlindLowVisionGoal(content: GenerationScopeContent): boolean {
  return asArray(content.characteristics).includes(BLIND_LOW_VISION_CHARACTERISTIC)
}

/**
 * Names what puts a saved anchor page out of scope for generation, or returns null when it
 * is in scope. Fails closed: unreadable content and unrecognised subjects are out of scope.
 */
export function generationBlockedReason(content: GenerationScopeContent | null | undefined): string | null {
  if (!content) {
    return UNNAMED_SUBJECT_REASON
  }

  // The characteristic is named ahead of any subject: the pipeline cannot serve these
  // goals at all, so naming the subject would send the teacher to change the wrong thing.
  if (isBlindLowVisionGoal(content)) {
    return BLIND_LOW_VISION_LABEL
  }

  const namedAlignments = asArray(content.subject_alignments).filter(isNamedSubjectAlignment)
  if (!namedAlignments.length) {
    return UNNAMED_SUBJECT_REASON
  }

  // Any one in-scope alignment admits the page: a reading goal that also touches science
  // is still a reading goal.
  if (namedAlignments.some(isAlignmentInScope)) {
    return null
  }

  return namedAlignments[0].subject
}

/**
 * The blocked reason for the catalog anchor page, where the goal is not saved yet. Drops
 * the unnamed-subject fallback, since "not yet available for this goal" would read as a
 * permanent no when the caller can tell the teacher to save first instead. A named
 * subject or characteristic is kept, because it stays true after saving.
 */
export function catalogBlockedReason(content: GenerationScopeContent | null | undefined): string | null {
  const reason = generationBlockedReason(content)
  if (reason === UNNAMED_SUBJECT_REASON) {
    return null
  }
  return reason
}
